class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

class ShippingRates extends HTMLElement {
    constructor() {
        super();

        this.form = this.querySelector('[data-form="shipping-rates"]')
        this.ratesList = this.querySelector('[data-shipping-rates]')
        this.zipCode = this.querySelector('[data-zip-code]')
        this.button = this.querySelector('[data-form-show]')
        this.message = this.querySelector('[data-message]')
        this.location = this.getAttribute('data-location')

        this.variantID = null;

        this.init()
    }

    init() {
        this.form.addEventListener("submit", (event) => this.submit(event))

        this.form.querySelector("#zip-code")
            .addEventListener("input", (event) => this.maskZipCode(event))

        this.button.addEventListener("click", (event) => this.reset(event))
    }

    reset() {
        this.showElement(this.form)
        this.hideElement(this.ratesList)
        this.hideElement(this.zipCode)
        this.hideElement(this.message)
    }

    async submit(event) {
        event.preventDefault();
        const { currentTarget } = event

        this.hideElement(this.message)
        const buttonSubmit = currentTarget.querySelector('button[type="submit"]');

        if (buttonSubmit) {
            buttonSubmit.setAttribute('disabled', 'true');
            buttonSubmit.innerHTML = 'Consultando...';
        }

        const cart = document.querySelector('cart-drawer')

        if (cart && cart.classList.contains('is-empty')) {
            const variantId = document.querySelector(`form[action="/cart/add"] input[name="id"]`).value

            try {
                await this.simulationCart(variantId)
            } catch (e) {
                buttonSubmit.removeAttribute('disabled');
                buttonSubmit.innerHTML = 'Consultar';

                this.displayError(e);
                return
            }
        }

        const formData = new FormData(currentTarget);
        const zip = formData.get('zip-code');

        try {
            const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${zip.replace(/\D/g, '')}`);
            const { state, cep } = await response.json();
            const shippingRates = await this.fetchShippingRates(state, cep);

            if (this.variantID) await this.removeItemCart(this.variantID)

            this.displayShippingRates(shippingRates);
            this.updateButton(zip);

            this.sendDataLayer({
                event: 'shipping_calculate',
                category: 'shipping',
                zipCode: cep,
                shippingRates: shippingRates.shipping_rates.map((shipping) => ({
                    label: shipping.name,
                    value: shipping.price,
                    currency: shipping.currency,
                    deadline: shipping.delivery_days[0] || null,
                    source: shipping.source,
                })),
                pageType: this.location,
                url: window.location.href
            })
        } catch (error) {
            console.warn({ error });
            this.displayError(error.message);
        }

        if (buttonSubmit) {
            buttonSubmit.removeAttribute('disabled');
            buttonSubmit.innerHTML = 'Consultar';
        }
    }

    async removeItemCart(variantId) {
        let updates = {
            [variantId]: 0
        };

        await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ updates })
        })
    }

    async simulationCart(variantId) {
        this.variantID = variantId;

        let formData = {
            'items': [{
                'id': variantId,
                'quantity': 1
            }]
        };

        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
        })

        const body = await response.json();

        if (body.status === 422) {
            throw new Error(body.message);
        }
    }

    updateButton(zip) {
        const container = this.zipCode;
        container.querySelector("span").textContent = zip;

        this.showElement(this.zipCode);
    }

    displayShippingRates(shippingRates) {
        if (!shippingRates || !shippingRates.shipping_rates || shippingRates.shipping_rates.length === 0) {
            throw new Error('Ops, CEP fora da área de entrega. Confira se o CEP está preenchido corretamente ou entre em contato com nosso atendimento.');
        }

        const fragment = document.createDocumentFragment();
        shippingRates.shipping_rates.forEach(rate => fragment.appendChild(this.createRateElement(rate)));

        this.ratesList.innerHTML = '';
        this.ratesList.appendChild(fragment);
        this.showElement(this.ratesList);
        this.hideElement(this.form)
    }

    createRateElement(rate) {
        const rateContainer = this.createElementWithClass('li', 'list-shipping-rates__item');
        const rateTitle = this.createElementWithClass('span', 'rate__title', rate.name);
        let rateContent;

        if (rate.delivery_days[0]) {
            rateContent = this.createElementWithClass('span', 'rate__content', `Em até ${rate.delivery_days[0]} dias úteis - R$ ${rate.price}`);
        } else {
            rateContent = this.createElementWithClass('span', 'rate__content', 'Frete grátis');
        }

        rateContainer.append(rateTitle, rateContent);

        return rateContainer;
    }

    createElementWithClass(tag, className, textContent = '') {
        const element = document.createElement(tag);
        element.classList.add(className);
        element.textContent = textContent;

        return element;
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async fetchShippingRates(province, zip) {
        const country = 'BR';
        const payload = JSON.stringify({
            shipping_address: { country, province, zip }
        });

        try {
            await this.requestShippingRates('/cart/prepare_shipping_rates', payload);
 // Aumentar o tempo de espera para dar tempo ao servidor processar
            await this.sleep(1500);
            // Adicionar tentativas múltiplas para a segunda requisição
            let attempts = 0;
            let maxAttempts = 3;
            let shippingRates = null;
            while (attempts < maxAttempts) {
                shippingRates = await this.requestShippingRates('/cart/async_shipping_rates');
                // Verificar se temos taxas de envio válidas
                if (shippingRates?.shipping_rates?.length) {
                    return shippingRates;
                }
                // Se não tiver taxas válidas, esperar e tentar novamente
                await this.sleep(1000);
                attempts++;
            }
            // Se chegou aqui, retorna o último resultado obtido
            return shippingRates;
        } catch (error) {
            console.error("Erro ao buscar taxas de envio:", error);
            throw new Error('Não conseguimos consultar, tente novamente ou entre em contato com nosso atendimento.');
        }
    }

    async requestShippingRates(url, body = null) {
        const options = body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body } : {};
        const response = await fetch(url, options);

        if (!response.ok) {
            throw new Error('Não conseguimos consultar, tente novamente ou entre em contato com nosso atendimento.');
        }

        return response.json();
    }

    showElement(element) {
        element.classList.remove('hidden');
    }

    hideElement(element) {
        element.classList.add('hidden');
    }

    maskZipCode(event) {
        const input = event.currentTarget

        if (input.value.length >= 9) {
            return
        }

        let cep = input.value.replace(/\D/g, '');

        if (cep.length > 5) {
            cep = cep.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
        }

        input.value = cep;
    }

    displayError(message) {
        this.message.textContent = message;
        this.showElement(this.message);
    }

    sendDataLayer(data) {
        try {
            Shopify.analytics.publish(PUB_SUB_EVENTS.shippingCalculate, data);
        } catch (error) {
            console.error("Erro ao enviar dados para o Data Layer:", error);
        }
    }
}

customElements.define('shipping-rates', ShippingRates);